import './DeviceScreen.scss';

import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import moment from 'moment';
import Grid from '@mui/material/Grid';
import HistoricalDateRangePanel from 'src/component/HistoricalDateRangePanel/HistoricalDateRangePanel';
import RefetchIcon from '@mui/icons-material/ReplaySharp';
import SettingsIcon from '@mui/icons-material/SettingsSharp';
import LivePill from 'src/component/UI/LivePill';
import NextIcon from '@mui/icons-material/ArrowForwardIosSharp';
import PreviousIcon from '@mui/icons-material/ArrowBackIosSharp';
import toast from 'src/utils/toast';
import Tooltip from '@mui/material/Tooltip';
import { Link } from 'react-router-dom';
import { fetchClient } from 'src/module/authentication/action';
import { getAuthenticatedUser, getPluralWorkTicketLabel } from 'src/module/authentication/selector';
import { getCurrentDevice, getDeviceStatus, getDeviceSettings, getDeviceConfigFields, getDeviceActions, getIsDeviceInCommFail, getSiblingsInfo, getLoadingDevicesWithGroupedAlarms, getDeviceConfigFieldsLoading, getLayoutConfig } from 'src/module/device/selector';
import { fetchDeviceStatus, fetchDeviceAlarms, fetchDeviceSettings, fetchDevicesActiveNotes, fetchDevicesArchivedNotes, fetchDevicesActiveWorkTickets, fetchDevicesArchivedWorkTickets, fetchStatistics, fetchDevicesAuditTrail, fetchDevicesActiveAlarms, fetchDevicesArchivedAlarms } from 'src/module/device/action';
import { getTemperatureUnit } from 'src/module/authentication/selector';
import BuildIcon from '@mui/icons-material/BuildSharp';
import { getSettingsModal } from 'src/module/device/selector';
import { setSettingsModalClose, setSettingsModalOpen } from 'src/module/device/action';
import { SettingsModalTypes } from 'src/component/UI/SettingsModal/SettingsModalTypes';
import variables from 'src/style/variable/variables.module.scss';
import NotFoundScreen from 'src/screen/Error/NotFoundScreen';
import DeviceProperties from './DeviceProperties';
import GaugeContainer from './GaugeContainer';
import DeviceStatusDetails from './DeviceStatusDetails';
import NotesContent from './NotesContent';
import DeviceStatistics from './DeviceStatistics';
import NotificationsContent from './NotificationsContent';
import AlarmsContent from './AlarmsContent';
import { getIsInactive } from 'src/module/user/selector';
import DeviceSensorGraph from './DeviceSensorGraph';
import useMediaQuery from '@mui/material/useMediaQuery';
import DeviceScreenActionButtons from './DeviceScreenActionButtons';
import { Divider, Modal, Paper } from '@mui/material';
import ChildDevices from './ChildDevices';
import Banner from 'src/component/UI/Banner';
import { deviceTypeConfig } from 'src/utils/deviceTypeConfig';
import AuditContent from './AuditContent';
import TestingReportsContent from './TestingReport/TestingReportsContent';
import { isWhitelabel } from 'src/utils/utils';
import Button from 'src/component/UI/Button';
import DataTable from 'src/component/UI/DataTable';
import { getSettingsDiff } from 'src/module/device/selector';
import PreviewIcon from '@mui/icons-material/PreviewSharp';
import SettingsMismatchModal from 'src/component/SettingsMismatchModal';
import ForcedOnContent from './ForcedOnContent';
import SpliceReportsContent from './SpliceReport/SpliceReportContent';
import CircuitFunctionTestReportContent from './CircuitFunctionTestReport/CircuitFunctionTestReportContent';
import InsulationInspectionTestReportContent from './InsulationInspectionTestReport/InsulationInspectionTestReportContent';
import GenericCommissioningContent from './Commissioning/GenericCommissioningContent';
import CommissioningCommLoopReport from './CommissioningCommLoop/CommissioningCommLoopReport';
import CommissioningCircuitReport from './CommissioningCircuit/CommissioningCircuitReport';
import CommissioningControllerReport from './CommissioningController/CommissioningControllerReport';
import CommissioningCablePreInsulationReport from './CommissioningCablePreInsulation/CommissioningCablePreInsulationReport';
import CommissioningCablePostInsulationReport from './CommissioningCablePostInsulation/CommissioningCablePostInsulationReport';
import DeviceBreakDown from './DeviceBreakDown';
import FeatureFence from 'src/component/FeatureFence';
import deviceBreakdown from 'src/assets/placeholders/device-breakdown.png';
import NoFeatureAccess from 'src/component/UI/NoFeatureAccess';
import { useTranslation } from 'src/i18n';
import { useDeviceDataLoader } from './useDeviceDataLoader';

// Resolve how a device's status should be displayed from its layout config.
// Known modes are 'gauges' (the default) and 'tiles' (the flat tile panel).
// An unknown/missing mode falls back to gauges, but logs so a misconfigured
// device factory surfaces loudly instead of silently rendering the default.
const resolveStatusDisplay = (layoutConfig) => {
  const display = layoutConfig?.getIn(['status', 'display']) ?? 'gauges';
  switch (display) {
    case 'gauges':
    case 'tiles':
      return display;
    default:
      console.warn(`Unknown status display mode '${display}'; falling back to gauges`); // eslint-disable-line no-console
      return 'gauges';
  }
};

export default function DeviceScreen (props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const deviceTagFromUrlDirty: string = decodeURIComponent(props.match.params.device_tag);
  const deviceTagFromUrl: string = deviceTagFromUrlDirty === '_' ? null : deviceTagFromUrlDirty;
  const childTagFromUrl: string = decodeURIComponent(props.match.params.child_tag);
  const urlDeviceTag = childTagFromUrl && childTagFromUrl !== 'undefined' ? childTagFromUrl : deviceTagFromUrl;
  const device = useSelector(getCurrentDevice(deviceTagFromUrl, childTagFromUrl));
  const isInactive = useSelector(getIsInactive(window.location.pathname));
  const pluralWorkTicketLabel = useSelector(getPluralWorkTicketLabel);
  const loadingDevice = useSelector(getLoadingDevicesWithGroupedAlarms);
  const [loading, setLoading] = useState(true);
  const [sensorHistoryRefreshToken, setSensorHistoryRefreshToken] = useState(0);
  const [previousCircuitTag, setPreviousCircuitTag]: any = useState(null);
  const [nextCircuitTag, setNextCircuitTag]: any = useState(null);

  const deviceId = device?.get('id');
  const deviceConfigFields = useSelector(getDeviceConfigFields(deviceId));
  const deviceConfigFieldsLoading = useSelector(getDeviceConfigFieldsLoading(deviceId));
  const layoutConfig = useSelector(getLayoutConfig(deviceId));
  const statusDisplay = resolveStatusDisplay(layoutConfig);
  const deviceActions = useSelector(getDeviceActions(deviceId));
  const isDeviceInCommFail = useSelector(getIsDeviceInCommFail(deviceId));
  const user = useSelector(getAuthenticatedUser);

  const deviceStatus = useSelector(getDeviceStatus(deviceId));
  const deviceSettings = useSelector(getDeviceSettings(deviceId));
  const temperatureUnit = useSelector(getTemperatureUnit);
  const settingsDiff = useSelector(getSettingsDiff(deviceId));
  const designedSettingsModal = useSelector(getSettingsModal(deviceId));
  const allSiblingCircuits = useSelector(getSiblingsInfo(device?.get('parent_id')));
  const designedSettingsModalIsLoading = designedSettingsModal?.get('loading');
  const designedSettingsModalIsOpen = designedSettingsModal?.get('isOpen');
  const designedSettingsModalId = designedSettingsModal?.get('id');

  const [lastUpdatedAt, setLastUpdatedAt] = useState(device?.getIn(['updated_at']));
  const statusTempSensors = device?.getIn(['status', 'temperatureSensors']) ?? Map({});
  const numberOfStatusSensors = Object.values(statusTempSensors?.toJS()).filter(x => x != null);
  const numTempSensorsStatus = numberOfStatusSensors.length || 1;
  const numTempSensorsSettings = device?.getIn(['settings', 'temperatureSensors'])?.size || 0;
  const numTemperatureSensors = Math.min(numTempSensorsStatus, numTempSensorsSettings);
  const [isDeviceLive, setIsDeviceLive] = useState(false);
  const [settingsAreUptoDate, setSettingsAreUptoDate] = useState(false);
  const isMobileOrIpad = useMediaQuery(`(max-width: ${variables.mobileWidth})`);
  const settingsModal = useSelector(getSettingsModal(deviceId));
  const deviceType = device?.get('type');
  const [settingsUpdatedAt, setSettingsUpdatedAt] = useState(lastUpdatedAt);
  const [statusUpdatedAt, setStatusUpdatedAt] = useState(lastUpdatedAt);
  const [viewConfigSettingsIssue, setViewConfigSettingsIssue] = useState(false);
  const isCircuit = device?.get('type') === 'circuit';
  const parentIsController = device?.get('parent_type') === 'controller';
  const deviceTag = device?.get('tag');

  const clickDesignedSettingsMismatchIcon = () => {
    dispatch(setSettingsModalOpen(deviceId, SettingsModalTypes['DESIGNED_SETTINGS_MISMATCH_MODAL']));
  };

  const closeDesignedSettingsModal = () => {
    dispatch(setSettingsModalClose(deviceId));
  };


  useEffect(() => {
    dispatch(fetchClient());
    dispatch(setSettingsModalClose(deviceId));
  }, []);

  // Use custom hook to load device data immediately (before render completes)
  // This starts fetching as soon as the component initializes, not after first render
  useDeviceDataLoader(
    device,
    deviceId,
    deviceTagFromUrl,
    childTagFromUrl,
    urlDeviceTag
  );

  // Update timestamps when data changes
  useEffect(() => {
    setLastUpdatedAt(deviceStatus?.get('updatedAt'));
    setStatusUpdatedAt(deviceStatus?.get('updatedAt'));
  }, [deviceStatus?.get('updatedAt')]);

  useEffect(() => {
    setLastUpdatedAt(deviceSettings?.get('updatedAt'));
    setSettingsUpdatedAt(deviceSettings?.get('updatedAt'));
  }, [deviceSettings?.get('updatedAt')]);

  useEffect(() => {
    if (device) {
      setLastUpdatedAt(device.getIn(['updated_at']));
    }
  }, [device?.getIn(['updated_at'])]);

  // Set loading to false once device is loaded
  useEffect(() => {
    if (deviceId) {
      setLoading(false);
    }
  }, [deviceId]);


  useEffect(() => {
    if (device && deviceTypeConfig[device.get('type')]?.activelyMonitored) {
      let interval;
      if (typeof device.getIn(['settings']) !== 'undefined' && !isInactive && !device.get('offline')) {
        if (deviceId) {
          dispatch(fetchDeviceStatus(deviceId));
        }

        interval = setInterval(() => {
          if (deviceId && !device.get('offline')) {
            dispatch(fetchDeviceStatus(deviceId));
            dispatch(fetchDeviceAlarms(deviceId));
          }
        }, device.get('monitoring_interval', 10000) || 10000);
      } else if (!isInactive) {
        return;
      }

      return () => {
        clearInterval(interval);
      };
    }

  }, [isInactive, deviceId]);

  const fetchDeviceAllData = () => {
    if (deviceId) {
      const paginationDetails = {
        filter: '',
        pageSize: 10,
        sort: 0,
        page: 0
      };
      if (device?.get('type') !== 'commLoop' && !device?.get('offline')) {
        if (isCircuit) {
          dispatch(fetchStatistics(deviceId));
          setSensorHistoryRefreshToken((n: number) => n + 1);
        }

        dispatch(fetchDeviceSettings(deviceId));
        dispatch(fetchDeviceStatus(deviceId));
        dispatch(fetchDevicesAuditTrail(deviceId,
          Map({
            page: 0,
            pageSize: 10,
            pageFilter: null,
            pageSort: null,
          })
        ));
      }

      const optionalFilters = Map({
        deviceId
      });

      dispatch(fetchDevicesActiveAlarms(Map({}), optionalFilters, 0, 10));
      dispatch(fetchDevicesArchivedAlarms(Map({}), optionalFilters, 0, 10));
      dispatch(fetchDevicesActiveNotes(null, Map({}), paginationDetails, false, deviceId));
      dispatch(fetchDevicesArchivedNotes(null, Map({}), paginationDetails, true, deviceId));
      dispatch(fetchDevicesActiveWorkTickets(false, null, 0, 10, null, null, deviceId));
      dispatch(fetchDevicesArchivedWorkTickets(false, null, 0, 10, null, null, deviceId));
      toast.success(t('device.fetchingData'));
    }
  };

  const deviceLastUpdated = () => {
    const timeNow: any = new Date();
    const statusTime: any = new Date(statusUpdatedAt);
    const statusTimeDiff: any = timeNow - statusTime;
    // for Live button
    if (statusTimeDiff < 120000) {
      setIsDeviceLive(true);
    } else {
      setIsDeviceLive(false);
    }

    const settingsTime: any = new Date(settingsUpdatedAt);
    const settingsTimeDiff: any = timeNow - settingsTime;

    // for advanced settings top buttton
    if (settingsTimeDiff < 3600000) {
      setSettingsAreUptoDate(true);
    } else {
      setSettingsAreUptoDate(false);
    }
  };

  useEffect(() => {
    deviceLastUpdated();

    const interval = setInterval(() => {
      deviceLastUpdated();
    }, 10000);
    return () => {
      clearInterval(interval);
    };
  }, [lastUpdatedAt]);


  const toggleAdvancedSettingsModal = () => {
    if (!settingsModal?.get('isOpen')) {
      dispatch(setSettingsModalOpen(deviceId, SettingsModalTypes.DEVICE_ADVANCED_SETTINGS));
    }
    else {
      dispatch(setSettingsModalClose(deviceId));
    }
  };

  const findAdjacentCircuits = (tag) => {
    let prev = null;
    let next = null;

    for (let i = 0; i < allSiblingCircuits?.size; i++) {
      const circuit: any = allSiblingCircuits.get(i);
      if (circuit?.get('tag') === tag) {
        const index = allSiblingCircuits.indexOf(circuit);
        prev = index > 0 ? allSiblingCircuits.get(index - 1) : allSiblingCircuits.get(allSiblingCircuits.size - 1);
        next = index < allSiblingCircuits.size - 1 ? allSiblingCircuits.get(index + 1) : allSiblingCircuits.get(0);
        break;
      }
    }
    if (prev) {
      setPreviousCircuitTag(prev.get('tag'));
    }

    if (next) {
      setNextCircuitTag(next.get('tag'));
    }
  };

  useEffect(() => {
    if (parentIsController && deviceTag && allSiblingCircuits?.size > 1) {
      findAdjacentCircuits(deviceTag);
    }
  }, [deviceTag, allSiblingCircuits.size]);

  const title = isWhitelabel() ? 'ATCOM' : 'SmartTrace';

  const [shouldShowNotFound, setShouldShowNotFound] = useState(false);

  useEffect(() => {
    if (!deviceId && !loadingDevice) {
      setShouldShowNotFound(true);
    }
    else {
      setShouldShowNotFound(false);
    }
  }, [deviceId, loadingDevice]);

  if (shouldShowNotFound) {
    return <NotFoundScreen />;
  }


  return (
    <div id='device-screen' className='page' data-testid='device-screen'>
      <Helmet>
        <title>{`${title}${device && deviceTypeConfig[device.get('type')]?.title ? ` - ${t(deviceTypeConfig[device.get('type')]?.title)}` : ''}`}</title>
      </Helmet>

      <SettingsMismatchModal
        open={designedSettingsModalIsOpen && designedSettingsModalId === SettingsModalTypes['DESIGNED_SETTINGS_MISMATCH_MODAL']}
        handleClose={() => closeDesignedSettingsModal()}
        settingsDiff={settingsDiff}
        deviceId={deviceId}
        loading={designedSettingsModalIsLoading}
      />

      <div className="device-screen-top-content">
        {
          parentIsController && previousCircuitTag ?
            <Tooltip title={previousCircuitTag || ''} >
              <Link
                className='prev-next-button'
                to={`/devices/${encodeURIComponent(device?.get('parent_tag'))}/${encodeURIComponent(previousCircuitTag)}`}
              >
                <PreviousIcon />
              </Link>
            </Tooltip>
            : null
        }
        <h2>{deviceTag}</h2>
        {
          parentIsController && nextCircuitTag ?
            <Tooltip title={nextCircuitTag || ''} >
              <Link
                className='prev-next-button'
                to={`/devices/${encodeURIComponent(device?.get('parent_tag'))}/${encodeURIComponent(nextCircuitTag)}`}
              >
                <NextIcon />
              </Link>
            </Tooltip>
            : null
        }

      </div><br />
      {
        deviceId &&
        <div className='top-action-buttons'>
          <Tooltip title={device?.get('offline') ? t('device.unableToFetch') : t('device.refetchData')}>
            {!device?.get('offline') ?
              <div className='device-screen-top-button' onClick={fetchDeviceAllData}>
                <RefetchIcon className='help-icon' style={{ fontSize: 16, color: 'white' }} />
              </div>
              :
              <div className='device-screen-top-button-offline device-offline' >
                <RefetchIcon className='help-icon' style={{ fontSize: 16, color: 'white' }} />
              </div>
            }
          </Tooltip>


          {deviceType !== 'commLoop' ?
            <>
              <Tooltip title={settingsUpdatedAt ? t('device.lastFetchedSettings', { time: moment(settingsUpdatedAt).format('YYYY-MM-DD hh:mm A') }) : t('device.notFetchedSettings')}>
                <div className={settingsAreUptoDate ? 'device-screen-top-button make-button-green' : 'device-screen-top-button make-button-grey'} style={{ cursor: deviceConfigFields?.get('settings').size > 0 ? 'pointer' : 'default' }} onClick={e => deviceConfigFields?.get('settings').size > 0 ? toggleAdvancedSettingsModal() : e.preventDefault()} >
                  <SettingsIcon className='help-icon' style={{ fontSize: 16, color: 'white' }} />
                </div>
              </Tooltip>
              {!device?.get('offline') ?
                <Tooltip title={statusUpdatedAt ? t('device.lastFetchedStatus', { time: moment(statusUpdatedAt).format('YYYY-MM-DD hh:mm A') }) : t('device.notFetchedStatus')}>
                  <span className="live-pill-wrapper">
                    <LivePill live={isDeviceLive} label={t('device.live')} />
                  </span>
                </Tooltip>
                :
                <div className='device-screen-top-button-offline'>
                  <div className='device-screen-live-title'>{t('common.offline')}</div>
                </div>
              }
            </> : null
          }
        </div>
      }


      {isDeviceInCommFail ? <Banner type='warning' message={t('device.commFailBanner')} /> : null}
      {device?.getIn(['status', 'banner'], null) ? <Banner
        type={device.getIn(['status', 'banner', 'type'], 'info')}
        message={device.getIn(['status', 'banner', 'message'], '')}
      />
        : null}
      {!isDeviceInCommFail && deviceSettings?.get('settingConfigurationErrors')?.size > 0 ? <Banner type='warning' message={t('device.invalidConfig')}
        button={
          <Button
            onClick={() => setViewConfigSettingsIssue(true)}
            icon={<BuildIcon />}
          />} /> : null}



      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Grid container direction="column" spacing={2}>
            {device && deviceTypeConfig[device.get('type')]?.properties && (
              <Grid item>
                <DeviceProperties
                  device={device}
                  permission={'edit-device'}
                  configFields={deviceConfigFields}
                  temperatureUnit={temperatureUnit}
                />
              </Grid>
            )}
            {device && deviceTypeConfig[device.get('type')]?.children && (
              <Grid item className='child-devices-grid'>
                <Paper>
                  <ChildDevices device={device} />
                </Paper>
              </Grid>
            )}
            {!isMobileOrIpad && device && deviceTypeConfig[device.get('type')]?.actionButtons && (
              <Grid item>
                <DeviceScreenActionButtons
                  device={device}
                  deviceConfigFields={deviceConfigFields}
                  loading={loading || deviceConfigFieldsLoading || loadingDevice}
                  deviceActions={deviceActions}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12} md={8}>
          <Grid container direction="column" spacing={3}>
            {device && deviceTypeConfig[device.get('type')]?.currentAlarms && (
              <Grid item>
                <AlarmsContent title={t('device.currentAlarms')} deviceId={deviceId} refetchInterval={10000} onlyActive />
              </Grid>
            )}
            <FeatureFence can={['programming-audit']}>
              {settingsDiff?.size > 0 && (
                <Grid item>
                  <div className='discrepancy-banner'
                    style={{ background: '#fcd54c', width: '100%', cursor: 'pointer', fontWeight: 'bold', textAlign: 'center', borderRadius: '4px', marginBottom: '25px' }}
                    onClick={clickDesignedSettingsMismatchIcon}>
                    {t('device.settingsMismatch')}
                    <PreviewIcon fontSize='large' style={{ verticalAlign: '-5px', marginLeft: '10px' }} />
                  </div>
                </Grid>
              )}
            </FeatureFence>
            {device && deviceTypeConfig[device.get('type')]?.gauges && statusDisplay === 'gauges' && (
              <Grid item>
                <GaugeContainer
                  isDeviceInCommFail={isDeviceInCommFail}
                  canDisableAllAlarms={device.getIn(['settings', 'allowDisableAllSetpoints'], false)}
                  canDisableControlAlarms={device.getIn(['settings', 'allowDisableControlSetpoints'], false)}
                  enableSpecificAlarms={device.getIn(['settings', 'enableSpecificAlarms'], null)}
                  device={device}
                  deviceStatus={deviceStatus}
                  deviceId={deviceId}
                  configFields={deviceConfigFields}
                  gaugeStyle={user?.getIn(['preferences', 'gaugeStyle'], 'radial')}
                />
              </Grid>
            )}
            {device && deviceTypeConfig[device.get('type')]?.gauges && statusDisplay === 'tiles' && (
              <Grid item>
                <DeviceStatusDetails deviceStatus={deviceStatus} layoutConfig={layoutConfig} />
              </Grid>
            )}

            {device && (deviceTypeConfig[device.get('type')]?.statistics || deviceTypeConfig[device.get('type')]?.sensorGraph) && (
              <Grid item>
                <HistoricalDateRangePanel
                  defaultPreset='7D'
                  stats={({ startDate, endDate }) => (
                    <FeatureFence can={['advanced-metrics']}>
                      {deviceTypeConfig[device.get('type')]?.statistics && (
                        <DeviceStatistics deviceId={deviceId} numTemperatureSensors={numTemperatureSensors} temperatureUnit={temperatureUnit} startDate={startDate} endDate={endDate} disablePaper />
                      )}
                    </FeatureFence>
                  )}
                  graph={({ startDate, endDate }) => (
                    <FeatureFence can={['status-monitoring']}>
                      {deviceTypeConfig[device.get('type')]?.sensorGraph && (
                        <>
                          <Divider />
                          <DeviceSensorGraph deviceId={deviceId} temperatureUnit={temperatureUnit} startDate={startDate} endDate={endDate} refreshToken={sensorHistoryRefreshToken} />
                        </>
                      )}
                    </FeatureFence>
                  )}
                />
              </Grid>
            )}
            <FeatureFence can={['work-tickets']}>
              {device && deviceTypeConfig[device.get('type')]?.currentNotifications && (
                <Grid item>
                  <NotificationsContent title={t('workTickets.currentTickets', { label: pluralWorkTicketLabel || t('workTickets.title') })} deviceId={deviceId} />
                </Grid>
              )}
            </FeatureFence>
            <FeatureFence can={['eht-notes']}>
              {device && deviceTypeConfig[device.get('type')]?.currentNotes && (
                <Grid item>
                  <NotesContent title={t('device.currentNotes')} deviceId={deviceId} showDeleteIcon />
                </Grid>
              )}
            </FeatureFence>
            {device && deviceTypeConfig[device.get('type')]?.deviceBreakDown && (
              <Grid item>
                <FeatureFence can='comm-loop-stats' noPermissionComponent={
                  <NoFeatureAccess
                    title={t('device.deviceBreakdown')}
                    placeholderImage={deviceBreakdown}
                    tooltipMessage={t('device.deviceBreakdownTooltip')}
                    placeholderAltText={t('device.deviceBreakdown')}
                  />
                }>
                  <DeviceBreakDown title={t('device.deviceBreakdown')} deviceId={deviceId} />
                </FeatureFence>
              </Grid>
            )}
            <FeatureFence can='alarm-historian'>
              {device && deviceTypeConfig[device.get('type')]?.pastAlarms && (
                <Grid item>
                  <AlarmsContent title={t('device.pastAlarms')} deviceId={deviceId} isExpanable onlyArchived />
                </Grid>
              )}
            </FeatureFence>
            <FeatureFence can={['closed-work-tickets']}>
              {device && deviceTypeConfig[device.get('type')]?.pastNotifications && (
                <Grid item>
                  <NotificationsContent title={t('workTickets.pastTickets', { label: pluralWorkTicketLabel || t('workTickets.title') })} deviceId={deviceId} isExpanable onlyArchived />
                </Grid>
              )}
            </FeatureFence>
            <FeatureFence can={['eht-notes']}>
              {device && deviceTypeConfig[device.get('type')]?.pastNotes && (
                <Grid item>
                  <NotesContent title={t('device.pastNotes')} deviceId={deviceId} isExpanable onlyArchived />
                </Grid>
              )}
            </FeatureFence>
            <FeatureFence can={['field-reports']}>
              {device && deviceTypeConfig[device.get('type')]?.testingReports && (
                <Grid item>
                  <TestingReportsContent deviceId={deviceId} />
                </Grid>
              )}
              {device && deviceTypeConfig[device.get('type')]?.spliceReports && (
                <Grid item>
                  <SpliceReportsContent deviceId={deviceId} />
                </Grid>
              )}
              {device && deviceTypeConfig[device.get('type')]?.circuitFunctionTestReports && (
                <Grid item>
                  <CircuitFunctionTestReportContent deviceId={deviceId} />
                </Grid>
              )}
              {device && deviceTypeConfig[device.get('type')]?.insulationInspectionTestReports && (
                <Grid item>
                  <InsulationInspectionTestReportContent deviceId={deviceId} />
                </Grid>
              )}
              {device && deviceTypeConfig[device.get('type')]?.commissioningCommLoopReports && (
                <Grid item>
                  <GenericCommissioningContent
                    deviceId={deviceId}
                    checkSheetType="commissioning-comm-loop-v2"
                    titleKey="commissioningCommLoop.title"
                    createReportButtonKey="commissioningCommLoop.createReport"
                    tableId="device-commissioning-comm-loop-table"
                    contentTestId="commissioning-comm-loop-content"
                    fetchTaskType="commissioning-comm-loop-v2-general"
                    ReportComponent={CommissioningCommLoopReport}
                  />
                </Grid>
              )}
              {device && deviceTypeConfig[device.get('type')]?.commissioningCircuitReports && (
                <Grid item>
                  <GenericCommissioningContent
                    deviceId={deviceId}
                    checkSheetType="commissioning-circuit"
                    titleKey="commissioningCircuit.title"
                    createReportButtonKey="commissioningCircuit.createReport"
                    tableId="device-commissioning-circuit-table"
                    contentTestId="commissioning-circuit-content"
                    fetchTaskType="commissioning-circuit-general"
                    ReportComponent={CommissioningCircuitReport}
                  />
                </Grid>
              )}
              {device && deviceTypeConfig[device.get('type')]?.commissioningControllerReports && (
                <Grid item>
                  <GenericCommissioningContent
                    deviceId={deviceId}
                    checkSheetType="commissioning-controller"
                    titleKey="commissioningController.title"
                    createReportButtonKey="commissioningController.createReport"
                    tableId="device-commissioning-controller-table"
                    contentTestId="commissioning-controller-content"
                    fetchTaskType="commissioning-controller-general"
                    ReportComponent={CommissioningControllerReport}
                  />
                </Grid>
              )}
              {device && deviceTypeConfig[device.get('type')]?.commissioningCablePreInsulationReports && (
                <Grid item>
                  <GenericCommissioningContent
                    deviceId={deviceId}
                    checkSheetType="commissioning-cable-pre-insulation"
                    titleKey="commissioningCablePreInsulation.title"
                    createReportButtonKey="commissioningCablePreInsulation.createReport"
                    tableId="device-commissioning-cable-pre-insulation-table"
                    contentTestId="commissioning-cable-pre-insulation-content"
                    fetchTaskType="commissioning-cable-pre-insulation-general"
                    ReportComponent={CommissioningCablePreInsulationReport}
                  />
                </Grid>
              )}
              {device && deviceTypeConfig[device.get('type')]?.commissioningCablePostInsulationReports && (
                <Grid item>
                  <GenericCommissioningContent
                    deviceId={deviceId}
                    checkSheetType="commissioning-cable-post-insulation"
                    titleKey="commissioningCablePostInsulation.title"
                    createReportButtonKey="commissioningCablePostInsulation.createReport"
                    tableId="device-commissioning-cable-post-insulation-table"
                    contentTestId="commissioning-cable-post-insulation-content"
                    fetchTaskType="commissioning-cable-post-insulation-general"
                    ReportComponent={CommissioningCablePostInsulationReport}
                  />
                </Grid>
              )}
            </FeatureFence>

            <FeatureFence can={['forced-on-logs']}>
              {device && deviceTypeConfig[device.get('type')]?.forcedOnLogs && (
                <Grid item>
                  <ForcedOnContent deviceId={deviceId} />
                </Grid>
              )}
            </FeatureFence>
            <FeatureFence can={['audit-trail']}>
              {device && deviceTypeConfig[device.get('type')]?.auditTrail && (
                <Grid item>
                  <AuditContent deviceId={deviceId} />
                </Grid>
              )}
            </FeatureFence>
            {isMobileOrIpad && device && deviceTypeConfig[device.get('type')]?.actionButtons && (
              <Grid item>
                <DeviceScreenActionButtons
                  device={device}
                  deviceConfigFields={deviceConfigFields}
                  loading={loading || deviceConfigFieldsLoading || loadingDevice}
                  deviceActions={deviceActions}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
      <Modal
        data-testid='view-settings-miss-configuration-modal'
        open={viewConfigSettingsIssue}
        onClose={() => setViewConfigSettingsIssue(false)}
        aria-labelledby='view-settings-miss-configuration-title'
      >
        <div className='modal view-settings-miss-configuration-modal'>
          <DataTable
            columns={[{
              id: 'setting',
              Header: t('device.settingMisconfiguration'),
              accessor: row => t('deviceValidationErrors.' + row),
              width: '500px'
            }]}
            data={deviceSettings?.get('settingConfigurationErrors')}
          />
          <div className='button-bar'>
            <Button onClick={() => setViewConfigSettingsIssue(false)}>{t('device.close')}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

DeviceScreen.propTypes = {
  match: PropTypes.object
};

DeviceScreen.defaultProps = {};
